import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useYoutubeVideos } from "@/hooks/useYoutubeVideos";

const fallbackImage =
  "https://readdy.ai/api/search-image?query=Football%20video%20thumbnail%20professional%20sports%20broadcast%20media%20production%20dark%20background&width=800&height=450&seq=video&orientation=landscape";

export default function BotafogoTV() {
  const navigate = useNavigate();
  const carouselRef = useRef<HTMLDivElement | null>(null);
  const { videos, liveVideo, loading, error } = useYoutubeVideos(12);
  const scrollCarousel = (direction: "left" | "right") => {
    if (!carouselRef.current) return;

    carouselRef.current.scrollBy({
      left: direction === "right" ? 360 : -360,
      behavior: "smooth",
    });
  };

  const videosParaExibir = liveVideo
    ? [liveVideo, ...videos.filter((video) => video.id !== liveVideo.id)]
    : videos;

  if (loading) {
    return (
      <section className="w-full py-8 md:py-12 bg-alvinegro-light">
        <div className="w-full px-4 md:px-8">
          <div className="h-8 w-44 bg-alvinegro-border rounded-lg mb-6 animate-pulse" />

          <div className="flex gap-4 overflow-hidden">
            {Array.from({ length: 4 }).map((_, index) => (
              <div
                key={index}
                className="min-w-[280px] md:min-w-[360px] space-y-3 animate-pulse"
              >
                <div className="aspect-video bg-alvinegro-border rounded-2xl" />
                <div className="h-4 bg-alvinegro-border rounded w-full" />
                <div className="h-3 bg-alvinegro-border rounded w-1/2" />
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="w-full py-8 md:py-12 bg-alvinegro-light">
        <div className="w-full px-4 md:px-8">
          <div className="bg-white border border-red-200 rounded-2xl p-6 text-red-600 text-sm">
            {error}
          </div>
        </div>
      </section>
    );
  }

  if (videosParaExibir.length === 0) {
    return null;
  }

  return (
    <section className="w-full py-8 md:py-12 bg-alvinegro-light">
      <div className="w-full px-4 md:px-8">
        <div className="flex items-center justify-between mb-6 md:mb-8">
          <div className="flex items-center gap-2">
            <i className="ri-youtube-line text-red-600 text-xl" />
            <h2 className="font-serif font-bold text-2xl md:text-3xl text-alvinegro-black">
              Botafogo TV
            </h2>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => scrollCarousel("left")}
              className="hidden md:flex w-9 h-9 rounded-full bg-white border border-alvinegro-border items-center justify-center hover:bg-alvinegro-border transition-colors"
              aria-label="Voltar vídeos"
            >
              <i className="ri-arrow-left-s-line" />
            </button>

            <button
              type="button"
              onClick={() => scrollCarousel("right")}
              className="hidden md:flex w-9 h-9 rounded-full bg-white border border-alvinegro-border items-center justify-center hover:bg-alvinegro-border transition-colors"
              aria-label="Avançar vídeos"
            >
              <i className="ri-arrow-right-s-line" />
            </button>

            <button
              onClick={() => navigate("/botafogo-tv")}
              className="text-sm font-medium text-alvinegro-muted hover:text-alvinegro-black transition-colors cursor-pointer whitespace-nowrap"
            >
              Ver todos
              <i className="ri-arrow-right-line ml-1" />
            </button>
          </div>
        </div>

        <div
          ref={carouselRef}
          className="flex gap-4 md:gap-6 overflow-x-auto scroll-smooth snap-x snap-mandatory pb-3 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {videosParaExibir.map((video) => {
            const isLive = liveVideo?.id === video.id;

            return (
              <button
                key={video.id}
                type="button"
                className="group cursor-pointer text-left min-w-[280px] sm:min-w-[320px] md:min-w-[360px] snap-start"
                onClick={() => navigate("/botafogo-tv")}
              >
                <div className="relative rounded-2xl overflow-hidden aspect-video mb-3 bg-alvinegro-border">
                  <img
                    src={video.thumbnail || fallbackImage}
                    alt={video.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />

                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />

                  {isLive && (
                    <span className="absolute top-3 left-3 bg-red-500 text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1">
                      <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
                      AO VIVO
                    </span>
                  )}

                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center group-hover:bg-white transition-colors">
                      <i
                        className={`${
                          isLive ? "ri-live-line" : "ri-play-fill"
                        } text-2xl text-red-600`}
                      />
                    </div>
                  </div>
                </div>

                <h3 className="font-semibold text-sm md:text-base text-alvinegro-black leading-snug line-clamp-2 group-hover:text-alvinegro-gray transition-colors">
                  {video.title}
                </h3>

                <div className="flex items-center gap-2 mt-1 text-xs text-alvinegro-muted">
                  <span>{isLive ? "Ao vivo agora" : "Botafogo TV"}</span>
                  <span>•</span>
                  <span>
                    {new Date(video.publishedAt).toLocaleDateString("pt-BR")}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}
